import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged } from 'rxjs';
import { DropdownPosition } from 'src/app/components/shared/form/components/form-select/form-select.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { VALIDATION_MESSAGE } from 'src/app/components/shared/shared-constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { AddDeviceAdmin, DeviceAdmin } from 'src/app/core/models/admin';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { CONSTS, DEVICE_IDS, ENABLED_OPTIONS } from './add-device.constants';

@Component({
  selector: 'app-add-device',
  templateUrl: './add-device.component.html',
  styleUrls: ['./add-device.component.scss'],
  standalone: false,
})
export class AddDeviceComponent implements OnInit {
  deviceForm!: FormGroup;
  initialForm!: FormGroup;

  inputData = inject<AddDeviceAdmin>(MAT_DIALOG_DATA);
  commonUtils = inject(CommonUtils);
  private dialogRef = inject(MatDialogRef<AddDeviceComponent>);
  private fb = inject(FormBuilder);

  readonly CONSTS = { ...CONSTS, ...VALIDATION_MESSAGE };
  readonly REGEX = SpecialCharacters;
  readonly DropdownPosition = DropdownPosition;
  readonly IDS = DEVICE_IDS;
  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly STATUS_OPTIONS = ENABLED_OPTIONS;

  ngOnInit() {
    this.initiateForm(this.inputData?.editData);
    this.subscribeToFields();
  }

  initiateForm(returnData?: DeviceAdmin): void {
    this.deviceForm = this.getInitialForm(returnData);
    this.initialForm = this.getInitialForm(returnData);
  }

  onSubmit() {
    this.dialogRef.close({
      data: this.deviceForm.getRawValue(),
      isEdit: this.inputData.editMode,
      callApi: true,
    });
  }

  clearText() {
    this.deviceForm = this.getInitialForm(
      this.initialForm.getRawValue() as DeviceAdmin,
    );
    this.deviceForm.updateValueAndValidity();

    // Re-subscribe to employeeId after resetting
    this.subscribeToFields();
  }

  clickExit() {
    this.commonUtils.openExitModal(this.deviceForm.dirty);
  }

  trim(id: string) {
    const control = this.deviceForm.get(id);
    control?.setValue(control.value.trim());
  }

  subscribeToFields() {
    const employeeIdControl = this.deviceForm.get(DEVICE_IDS.EMPLOYEE_ID);
    const driverIdControl = this.deviceForm.get(DEVICE_IDS.DRIVER_ID);

    employeeIdControl?.valueChanges
      .pipe(distinctUntilChanged((prev, curr) => prev?.trim() === curr?.trim()))
      .subscribe((currentValue: string) => {
        const trimmed = currentValue?.trim();

        if (!trimmed) {
          // Only clear Driver ID if the user hasn't typed anything into it
          if (!driverIdControl?.value?.trim()) {
            driverIdControl?.reset('', { emitEvent: false });
            driverIdControl?.updateValueAndValidity({ emitEvent: false });
          }
          return;
        }

        // Only auto-fill if Driver ID is currently empty
        const last10 = trimmed.slice(-10);
        if (driverIdControl?.value !== last10) {
          driverIdControl?.setValue(last10, { emitEvent: false });
          driverIdControl?.updateValueAndValidity({ emitEvent: false });
        }
      });
  }

  private getInitialForm(returnData?: DeviceAdmin) {
    return this.fb.group({
      [DEVICE_IDS.EMPLOYEE_ID]: [
        returnData?.employeeId?.toUpperCase() ?? '',
        Validators.compose([Validators.required]),
      ],
      [DEVICE_IDS.DRIVER_ID]: [
        returnData?.driverId?.toUpperCase() ?? '',
        Validators.compose([Validators.required]),
      ],
      [DEVICE_IDS.EMAIL]: [
        returnData?.email?.toUpperCase() ?? '',
        Validators.compose([Validators.required, Validators.email]),
      ],
      [DEVICE_IDS.LAST_NAME]: [
        returnData?.lastName?.toUpperCase() ?? '',
        Validators.compose([Validators.required]),
      ],
      [DEVICE_IDS.FIRST_NAME]: [
        returnData?.firstName?.toUpperCase() ?? '',
        Validators.compose([Validators.required]),
      ],
      [DEVICE_IDS.LOCATION]: [this.inputData.currLocation],
      [DEVICE_IDS.ENABLED]: [
        returnData?.enabled ?? true,
        Validators.compose([Validators.required]),
      ],
      //Non-editable
      [DEVICE_IDS.ID]: [returnData?.id],
      [DEVICE_IDS.LAST_LOGGED_IN]: [returnData?.lastLoggedIn],
      [DEVICE_IDS.INSERT_TIME]: [returnData?.insertTime],
      [DEVICE_IDS.ADDED_BY]: [returnData?.addedBy?.toUpperCase()],
      [DEVICE_IDS.OS]: [returnData?.os],
      [DEVICE_IDS.BUILD_VERSION]: [returnData?.buildVersion],
      [DEVICE_IDS.MODEL]: [returnData?.model],
    });
  }
}
